// libs
import { styled } from '@nykaa/ui-components';
import { css, keyframes } from '@emotion/core';

//  mixins
import { truncate } from 'shared/styles/mixin/core';

// constants
import { BREAKPOINTS } from 'shared/styles/base';
import { ASPECT_RATIO } from './constants';

//components
import MultiTags from 'shared/components/MultiTags';
import BaseColorOptions from 'shared/components/ColorOptions';

//types
import { CAROUSEL_STATES } from './types';

export const FALLBACK_ASPECT_RATIO_CLASS = 'aspect-ratio';
export const CAROUSEL_CLASS = 'carousel-list';

const autoplayAnimation = keyframes`
 from{
    opacity:1.0; 
  }
 
  to{
    opacity: 0.0;
  }
`;

export const ProductAnchor = styled.a<{
  carouselState: CAROUSEL_STATES;
  containsResponsible: boolean;
}>`
  text-decoration: none;
  flex-direction: column;
  display: flex;
  position: relative;
  text-align: center;
  padding: 0;
  cursor: pointer;
 ${({ containsResponsible, theme }) =>
   containsResponsible
     ? `
    height: calc(100% - ${theme.spacing.spacing100});
    margin-top: ${theme.spacing.spacing100};
 `
     : `
  height: 100%;
 `}

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    &:hover {
      box-shadow: ${({ theme }) => theme.elevations.shadow400};
      z-index: 1;
    }
  }

  .product-tag {
    margin-top: 0;
    padding:  ${({ theme }) => theme.spacing.spacing10}  ${({ theme }) => theme.spacing.spacing20};
   
    .tag-title {
      ${({ theme }) => theme.typography.labelSmall};
    }
  }
  ${({ carouselState }) =>
    carouselState === CAROUSEL_STATES.AUTOPLAY &&
    css`
      &:hover {
        .carousel-arrow {
          display: flex;
        }
        .image-fade-in {
          animation: 1.5s ${autoplayAnimation} 1.5s ease-in;
        }
        .carousel-dots {
          display: block;
        }
      }
    `};
    ${({ carouselState }) =>
      carouselState === CAROUSEL_STATES.FOCUS &&
      `
      &:hover{
        .carousel-arrow{
          display: flex;
        }
        .image-fade-in {
          animation: none;
        }
        .carousel-dots {
          display: block;
        }
      }
    `};
  }
`;

export const ProductImageContainer = styled.div`
  aspect-ratio: ${ASPECT_RATIO};
  position: relative;
  overflow: hidden;

  @supports not (aspect-ratio: 0) {
    padding-top: ${100 / ASPECT_RATIO}%;
    height: 0;

    .carousel-list {
      height: 100%;
      width: 100%;
    }

    .${FALLBACK_ASPECT_RATIO_CLASS} {
      position: absolute;
      transform: translate(-50%, 0);
      left: 50%;
      top: 0;
      max-width: 100%;
    }
  }
`;

export const Description = styled.div`
  position: relative;
  text-align: left;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-start;
  padding: ${({ theme }) =>
    `${theme.spacing.spacing20} ${theme.spacing.spacing40} ${theme.spacing.spacing40}`};
`;

export const SoldOut = styled.span`
  position: absolute;
  height: 26px;
  left: 50%;
  transform: translateX(-50%);
  top: -61px;
  color: ${({ theme }) => theme.colors.surface};
  ${({ theme }) => theme.typography.bodyLarge};
  background-color: #000;
  display: inline-block;
  text-align: center;
  width: 90px;
  border-radius: 2px;
`;

export const Title = styled.div`
  ${truncate}

  ${({ theme }) => theme.typography.subTitleMedium};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.spacing10};
`;

export const SubTitle = styled.div`
  ${truncate};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.spacing40};
  ${({ theme }) => theme.typography.bodyMedium};
`;

export const OfferMessage = styled.div`
  color: ${({ theme }) => theme.colors.positive};
  ${({ theme }) => theme.typography.bodySmall}
  margin-top: ${({ theme }) => theme.spacing.spacing20}
`;

export const SizeSoldout = styled.div`
  color: ${({ theme }) => theme.colors.warning};
  ${({ theme }) => theme.typography.bodySmall}
  margin-top: ${({ theme }) => theme.spacing.spacing20}
`;

export const Tags = styled(MultiTags)`
  margin-top: ${({ theme }) => theme.spacing.spacing40};
  padding: ${({ theme }) => `0 ${theme.spacing.spacing40}`};
  margin-left: 0;
`;

export const ImageChildrenContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 1;
`;

export const ColorOptions = styled(BaseColorOptions)`
  margin-top: -${({ theme }) => theme.spacing.spacing20};
`;
